import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { PackageCheck, Search, Loader2, Undo2, CheckCircle2, Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { apiError, SPORT_LABEL, maskCpf } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function KitPickup() {
  const { user } = useAuth();
  const canOperate = user?.role === "SUPER_ADMIN" || (user?.permissions || []).includes("OPERATOR");
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const inputRef = useRef(null);

  const loadStats = () => api.get("/staff/kit-stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); inputRef.current?.focus(); }, []);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/registrations", { params: { q: q.trim(), status: "PAID" } });
      setList(data.registrations);
      if (!data.registrations.length) toast.info("Nenhuma inscrição paga encontrada");
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const toggle = async (reg) => {
    setBusy(reg.id);
    try {
      const { data } = await api.patch(`/registrations/${reg.id}/kit`);
      setList((l) => l.map((r) => (r.id === reg.id ? data : r)));
      toast.success(data.kit_picked_at ? `Kit entregue: ${data.bib_code}` : "Retirada desfeita");
      loadStats();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  const pct = stats?.paid ? Math.round((stats.picked / stats.paid) * 100) : 0;

  const printLabels = async (sport) => {
    try {
      const res = await api.get("/staff/kit-labels", { params: sport ? { sport_type: sport } : {}, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="kit-pickup-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-brand font-bold uppercase tracking-widest text-xs">Check-in</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase">Retirada de Kits</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="kit-labels-all-btn" onClick={() => printLabels()} className="bg-white/5 border border-white/10 hover:bg-white/10 font-bold"><Printer className="w-4 h-4 mr-2" /> Etiquetas (todas)</Button>
          {["CORRIDA", "CICLISMO", "KIDS"].map((s) => (
            <Button key={s} data-testid={`kit-labels-${s}-btn`} onClick={() => printLabels(s)} variant="ghost" className="border border-white/10 hover:bg-white/10 text-xs font-bold uppercase">{SPORT_LABEL[s]}</Button>
          ))}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-3">
          {[["Pagos", stats.paid, "text-white"], ["Kits entregues", stats.picked, "text-neon"], ["Pendentes", stats.pending, "text-amber-300"]].map(([l, v, c]) => (
            <div key={l} className="rounded-2xl bg-surface border border-white/10 p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-400">{l}</div>
              <div className={`font-display text-3xl font-black ${c}`} data-testid={`kit-stat-${l.split(" ")[0].toLowerCase()}`}>{v}</div>
            </div>
          ))}
          <div className="col-span-3 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
      )}

      <form onSubmit={search} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
          <Input ref={inputRef} data-testid="kit-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Nº de peito (ex.: M1-105, CF-812, K-07), nome ou CPF" className="pl-9 bg-surface border-white/10 h-11 font-mono" />
        </div>
        <Button data-testid="kit-search-btn" type="submit" disabled={loading} className="bg-brand hover:bg-brand-hover font-bold uppercase h-11 px-6">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}
        </Button>
      </form>

      <div className="space-y-3">
        {list.map((reg) => {
          const picked = !!reg.kit_picked_at;
          return (
            <div key={reg.id} data-testid={`kit-row-${reg.bib_code}`}
              className={`rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-4 ${picked ? "bg-emerald-500/10 border-emerald-500/30" : "bg-surface border-white/10"}`}>
              <div className="flex-1 min-w-0">
                <div className="font-mono font-bold text-xl text-neon tracking-widest">{reg.bib_code}</div>
                <div className="font-semibold truncate">{reg.runner?.full_name}</div>
                <div className="text-xs text-slate-400">CPF {maskCpf(reg.runner?.cpf || "")} • {SPORT_LABEL[reg.category?.sport_type]} — {reg.category?.name}{reg.shirt_size ? ` • Camisa ${reg.shirt_size}` : ""}</div>
                {picked && <div className="text-xs text-emerald-300 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Entregue em {new Date(reg.kit_picked_at).toLocaleString("pt-BR")} por {reg.kit_picked_by}</div>}
              </div>
              {canOperate && (
                <Button data-testid={`kit-toggle-${reg.bib_code}`} onClick={() => toggle(reg)} disabled={busy === reg.id}
                  className={picked ? "bg-white/5 border border-white/10 hover:bg-white/10" : "bg-brand hover:bg-brand-hover font-bold uppercase"}>
                  {busy === reg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : picked ? <><Undo2 className="w-4 h-4 mr-2" /> Desfazer</> : <><PackageCheck className="w-4 h-4 mr-2" /> Confirmar retirada</>}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
